import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import Modal from 'modules/views/shared/Modal';
import TextField from 'modules/views/shared/TextField';
import useSubmitOnEnter from 'hooks/useSubmitOnEnter';
import { IContact } from 'modules/state-mgmt/workerData';
import { STYLE } from 'constants/style';

interface IRemoveContactModalProps {
  isVisible: boolean;
  onClose: () => void;
  contact: IContact | null;
  isLoading: boolean;
  error: string;
  isAdmin: boolean;
  onConfirm: (password: string) => void;
}

export const RemoveContactModal: React.FC<IRemoveContactModalProps> = ({
  isVisible,
  onClose,
  contact,
  isLoading,
  error,
  isAdmin,
  onConfirm,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [password, setPassword] = useState('');

  useEffect(() => {
    if (!isVisible) {
      setPassword('');
    }
  }, [isVisible]);

  const handleConfirm = () => {
    onConfirm(password);
  };
  const submitOnEnter = useSubmitOnEnter(handleConfirm, !password || isLoading);

  const modalTitle =
    contact?.contactType === 0
      ? t('myProfile.loginInformation.removeModal.emailTitle')
      : t('myProfile.loginInformation.removeModal.mobileTitle');

  const content = (
    <Box className={classes.content}>
      <Typography className={classes.heading}>{t('myProfile.loginInformation.removeModal.heading')}</Typography>
      <Typography className={classes.description}>
        {t('myProfile.loginInformation.removeModal.descriptionPrefix')}{' '}
        <span className={classes.contactValue}>{contact?.contactValue}</span>?
      </Typography>
      <TextField
        type={isAdmin ? 'text' : 'password'}
        maskText={isAdmin}
        placeholder={t('myProfile.loginInformation.removeModal.passwordPlaceholder')}
        value={password}
        testId="remove-contact-password-input"
        onChange={e => setPassword(e.target.value)}
        onKeyDown={submitOnEnter}
        fullWidth
        autoComplete={isAdmin ? 'off' : 'current-password'}
      />
      {!!error && (
        <Typography className={classes.errorText} data-testid="remove-contact-error">
          {error}
        </Typography>
      )}
    </Box>
  );

  return (
    <Modal
      title={modalTitle}
      isVisible={isVisible}
      Content={content}
      testId="remove-contact-modal"
      disableBackdropClose={isLoading}
      actionButtons={[
        {
          label: t('common.cancel'),
          variant: 'outlined',
          testId: 'remove-contact-cancel-button',
          onClick: onClose,
          disabled: isLoading,
        },
        {
          label: t('common.confirm'),
          testId: 'remove-contact-confirm-button',
          onClick: handleConfirm,
          isLoading,
          disabled: !password || isLoading,
        },
      ]}
      onClose={onClose}
    />
  );
};

const useStyles = makeStyles(() => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    minWidth: '420px',
    '@media (max-width: 600px)': {
      minWidth: 'unset',
    },
  },
  heading: {
    fontFamily: STYLE.FONT.PRIMARY_FONT,
    fontSize: '20px',
    fontWeight: 700,
    lineHeight: '28px',
    color: STYLE.COLOR.TEXT1,
  },
  description: {
    fontFamily: STYLE.FONT.PRIMARY_FONT,
    fontSize: '14px',
    fontWeight: 400,
    lineHeight: '22px',
    color: STYLE.COLOR.TEXT1,
  },
  contactValue: {
    color: STYLE.COLOR.ACCENT_PRIMARY,
    fontWeight: 600,
  },
  errorText: {
    fontFamily: STYLE.FONT.PRIMARY_FONT,
    fontSize: '12px',
    color: STYLE.COLOR.ERROR,
    marginTop: '-8px',
  },
}));
